import React, { useState } from "react";
import useNumericInput from "../../../hooks/useNumericInput";
import useUsernameInput from "../../../hooks/useUsernameInput";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import CouponAPI from "../../../api/promo/couponApi";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";

function AddNew() {
  useNumericInput();
  useUsernameInput();

  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split("T")[0];
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    code: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    maximumDiscount: "",
    minimumCartValue: "",
    startDate: "",
    endDate: "",
    usageLimit: "",
    usageLimitPerPerson: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    console.log(formData);

    if (!formData.title) {
      newErrors.title = "Title is required";
    }

    if (!formData.code) {
      newErrors.code = "Code is required";
    }

    if (!formData.usageLimit) {
      newErrors.usageLimit = "Usage Limit is required";
    }

    if (!formData.maximumDiscount) {
      newErrors.maximumDiscount = "Maximum Discount is required";
    }

    if (!formData.minimumCartValue) {
      newErrors.minimumCartValue = "Minimum Cart Value is required";
    }

    console.log(newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    showLoader();

    try {
      const res = await CouponAPI.addDetails(formData);

      if (res.code === 200) {
        showSuccess(res.message);

        setFormData({
          title: "",
          code: "",
          percentage: "",
          maximumDiscount: "",
          minimumCartValue: "",
          startDate: "",
          endDate: "",
          usageLimit: "",
          usageLimitPerPerson: "",
          description: "",
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Create New Coupon</h4>
            <h6>
              Set up discount codes with custom rules and restrictions to boost
              your sales.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Coupon Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <h5 className="mb-1">Basic Information</h5>
              <div className="row mb-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Coupon Title
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Title (e.g., Diwali Offer)"
                    className="form-control"
                    name="title"
                    onChange={handleChange}
                    value={formData.title}
                  />
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>
              </div>

              <h5 className="mb-1">Discount Information</h5>
              <div className="row">
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Coupon Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Code (e.g., DIWALI10)"
                      className="form-control only-username"
                      name="code"
                      onChange={handleChange}
                      value={formData.code}
                    />
                  </div>
                  {errors.code && (
                    <span className="errors text-danger">{errors.code}</span>
                  )}
                </div>
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Discount Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="discountType"
                      value={formData.discountType}
                      onChange={(e) =>
                        setFormData({
                          discountType: e.target.value,
                          discountValue: "", // reset on change
                        })
                      }
                    >
                      <option value="PERCENTAGE">Percentage (%)</option>
                      <option value="FLAT">Flat Amount (₹)</option>
                    </select>
                    {errors.discountType && (
                      <span className="errors text-danger">
                        {errors.discountType}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      {formData.discountType === "PERCENTAGE"
                        ? "Percentage (%)"
                        : "Flat Discount (₹)"}{" "}
                      <span className="text-danger">*</span>
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      placeholder={
                        formData.discountType === "PERCENTAGE"
                          ? "e.g. 10"
                          : "e.g. 500"
                      }
                      name="discountValue"
                      value={formData.discountValue || ""}
                      onChange={(e) => {
                        const val = e.target.value;

                        // percentage validation
                        if (formData.discountType === "PERCENTAGE") {
                          if (val === "" || (val >= 1 && val <= 100)) {
                            setFormData((p) => ({ ...p, discountValue: val }));
                          }
                        }
                        // flat amount
                        else {
                          if (val === "" || val >= 1) {
                            setFormData((p) => ({ ...p, discountValue: val }));
                          }
                        }
                      }}
                    />
                  </div>
                  {errors.discountvalue && (
                    <span className="errors text-danger">
                      {errors.discountvalue}
                    </span>
                  )}
                </div>

                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Maximum Discount Amount
                    </label>
                    <input
                      type="text"
                      placeholder="Amount (e.g., 100)"
                      className="form-control only-numeric"
                      name="maximumDiscount"
                      onChange={handleChange}
                      value={formData.maximumDiscount}
                    />
                  </div>
                  {errors.maximumdiscount && (
                    <span className="errors text-danger">
                      {errors.maximumdiscount}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">Minimum Cart Value</label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 500)"
                      className="form-control only-numeric"
                      name="minimumCartValue"
                      onChange={handleChange}
                      value={formData.minimumCartValue}
                    />
                  </div>
                  {errors.minimumcartvalue && (
                    <span className="errors text-danger">
                      {errors.minimumcartvalue}
                    </span>
                  )}
                </div>
              </div>

              <h5 className="mb-1">Valid Information</h5>
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">
                      Start Date <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      min={today}
                      value={formData.startDate}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.startdate && (
                    <span className="errors text-danger">
                      {errors.startdate}
                    </span>
                  )}
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">
                      End Date <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.endDate}
                      min={formData.startDate || today}
                      name="endDate"
                      onChange={handleChange}
                    />
                  </div>
                  {errors.enddate && (
                    <span className="errors text-danger">{errors.enddate}</span>
                  )}
                </div>
                <div className="col-lg-3">
                  <div className="mb-3">
                    <label className="form-label">
                      Usage Limit <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 1000)"
                      className="form-control only-numeric"
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleChange}
                    />
                    {errors.usagelimit && (
                      <span className="text-danger errors">
                        {errors.usagelimit}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="mb-3">
                    <label className="form-label">Usage Limit Per User</label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 1)"
                      className="form-control only-numeric"
                      name="usageLimitPerPerson"
                      value={formData.usageLimitPerPerson}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  ></textarea>
                  {errors.description && (
                    <span className="text-danger errors">
                      {errors.description}
                    </span>
                  )}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
